import React, { useEffect, useState } from "react";

import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import { Field, Fieldset, FieldGroup, Label } from "../../../design/fieldset";
import { useCase } from "../../../contexts/CaseContext";
import { Case } from "@visitproof/types";

interface CourtAddressFormData {
  county: string;
  streetAddress: string;
  mailingAddress: string;
  city: string;
  zipCode: string;
  branchName: string;
}

interface Props {
  caseItem: Case;
}

const EditCourtAddressButton: React.FC<Props> = ({ caseItem }) => {
  const { updateCase } = useCase();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CourtAddressFormData>({
    county: "",
    streetAddress: "",
    mailingAddress: "",
    city: "",
    zipCode: "",
    branchName: "",
  });

  // Initialize form data from case item
  useEffect(() => {
    if (caseItem.courtAddress) {
      setFormData({
        county: caseItem.courtAddress.county ?? "",
        streetAddress: caseItem.courtAddress.streetAddress ?? "",
        mailingAddress: caseItem.courtAddress.mailingAddress ?? "",
        city: caseItem.courtAddress.city ?? "",
        zipCode: caseItem.courtAddress.zipCode ?? "",
        branchName: caseItem.courtAddress.branchName ?? "",
      });
    }
  }, [caseItem.courtAddress]);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      if (caseItem.courtAddress) {
        setFormData({
          county: caseItem.courtAddress.county ?? "",
          streetAddress: caseItem.courtAddress.streetAddress ?? "",
          mailingAddress: caseItem.courtAddress.mailingAddress ?? "",
          city: caseItem.courtAddress.city ?? "",
          zipCode: caseItem.courtAddress.zipCode ?? "",
          branchName: caseItem.courtAddress.branchName ?? "",
        });
      } else {
        setFormData({
          county: "",
          streetAddress: "",
          mailingAddress: "",
          city: "",
          zipCode: "",
          branchName: "",
        });
      }
    }
  }, [isOpen, caseItem.courtAddress]);

  const handleInputChange = (field: keyof CourtAddressFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const validateForm = (): boolean => {
    // Basic validation - require at least some address information
    const hasAddressInfo = formData.streetAddress.trim() || 
                          formData.mailingAddress.trim() || 
                          formData.city.trim();
    
    if (!hasAddressInfo) {
      setError("Please provide at least one address field (street address, mailing address, or city)");
      return false;
    }

    // Validate zip code format if provided
    if (formData.zipCode.trim() && !/^\d{5}(-\d{4})?$/.test(formData.zipCode.trim())) {
      setError("Please enter a valid zip code (e.g., 12345 or 12345-6789)");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!caseItem.id) {
      setError("Case ID is required");
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      await updateCase(caseItem.id, {
        courtAddress: {
          county: formData.county.trim(),
          streetAddress: formData.streetAddress.trim(),
          mailingAddress: formData.mailingAddress.trim(),
          city: formData.city.trim(),
          zipCode: formData.zipCode.trim(),
          branchName: formData.branchName.trim(),
        },
      });
      
      handleClose();
    } catch (error) {
      console.error("Failed to update case:", error);
      setError("Failed to update court address. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setError(null);
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    handleClose();
  };

  const hasExistingAddress = Boolean(caseItem.courtAddress);

  return (
    <>
      {hasExistingAddress ? (
        <Button
          type="button"
          outline
          onClick={() => setIsOpen(true)}
          aria-label="Edit court address"
        >
          Edit
        </Button>
      ) : (
        <Button
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label="Add court address"
        >
          Add Court Address
        </Button>
      )}

      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle>
          {hasExistingAddress ? "Edit Court Address" : "Add Court Address"}
        </DialogTitle>
        
        <DialogBody>
          {error && (
            <div className="p-3 mb-4 border border-red-200 rounded-md bg-red-50">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label htmlFor="county">County</Label>
                <Input
                  id="county"
                  value={formData.county}
                  onChange={(e) => handleInputChange("county", e.target.value)}
                  placeholder="Enter county name"
                  disabled={isSubmitting}
                />
              </Field>
              
              <Field>
                <Label htmlFor="streetAddress">Street Address</Label>
                <Input
                  id="streetAddress"
                  value={formData.streetAddress}
                  onChange={(e) => handleInputChange("streetAddress", e.target.value)}
                  placeholder="Enter street address"
                  disabled={isSubmitting}
                />
              </Field>
              
              <Field>
                <Label htmlFor="mailingAddress">Mailing Address</Label>
                <Input
                  id="mailingAddress"
                  value={formData.mailingAddress}
                  onChange={(e) => handleInputChange("mailingAddress", e.target.value)}
                  placeholder="Enter mailing address (if different from street address)"
                  disabled={isSubmitting}
                />
              </Field>
              
              <Field>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleInputChange("city", e.target.value)}
                  placeholder="Enter city name"
                  disabled={isSubmitting}
                />
              </Field>
              
              <Field>
                <Label htmlFor="zipCode">Zip Code</Label>
                <Input
                  id="zipCode"
                  value={formData.zipCode}
                  onChange={(e) => handleInputChange("zipCode", e.target.value)}
                  placeholder="Enter zip code (e.g., 12345)"
                  disabled={isSubmitting}
                />
              </Field>
              
              <Field>
                <Label htmlFor="branchName">Branch Name</Label>
                <Input
                  id="branchName"
                  value={formData.branchName}
                  onChange={(e) => handleInputChange("branchName", e.target.value)}
                  placeholder="Enter court branch name"
                  disabled={isSubmitting}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        
        <DialogActions>
          <Button 
            plain 
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            disabled={isSubmitting} 
            onClick={handleSubmit}
            aria-label={isSubmitting ? "Saving court address" : "Save court address"}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditCourtAddressButton;
